import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { LoadingDialogComponent } from "@cotecna/dialogs";
import { FileUploadOptions } from "@cotecna/file-management";
import {
  Field,
  FormComponent,
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
  Section,
} from "@cotecna/dynamic-view-engine";
import { PresetsService } from "src/app/settings/services/presets.service";
import { ImportDialogData } from "./import-dialog-data";

const fileUploadOptions = <FileUploadOptions>{
  allowedExtensions: [".xls", ".xlsx"],
  chipsVisible: true,
  customPlaceholder: "Drag files or click to upload",
  disabled: false,
  singleFile: true,
  tableMode: false,
};

@Component({
    selector: "im-import-dialog",
    templateUrl: "./import-dialog.component.html",
    styleUrls: ["./import-dialog.component.scss"],
    standalone: false
})
export class ImportDialogComponent implements OnInit {
  constructor(
    private dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private presetService: PresetsService,
    private dialog: MatDialog,
  ) {}

  @ViewChild("naturalForm")
  form: FormComponent;

  public file: File;
  public fileUploadOptions = fileUploadOptions;
  public naturalFormConfig: NaturalFormConfig = <NaturalFormConfig>{
    displayMode: NaturalFormStyle.material,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: true,
    returnType: FormReturnType.entity,
  };
  public info: ImportDialogData[] = [];
  public canImport: boolean = false;
  public sections: Section[];
  public controls: any[];
  public disableDownload: boolean = true;
  public isDownloadTemplateVisible: boolean = false;
  private defaultFormId: string;

  ngOnInit(): void {
    this.sections = JSON.parse(JSON.stringify(this.data.sections));
    this.controls = JSON.parse(JSON.stringify(this.data.controls));
    this.defaultFormId = this.data.defaultFormId;
    if (this.defaultFormId) this.fillDefaultForm();

    if (this.data.title === "Import Items") {
      this.isDownloadTemplateVisible = true;
    }
  }

  ngAfterViewInit(): void {
    if (this.form.fieldChange) {
      this.form.fieldChange.subscribe((field: Field) => {
        let existingData = this.info?.find((i) => i.id == field.id);
        if (existingData) {
          existingData.value = field.value;
          existingData.name = field.readableValue;
        } else {
          if (this.sections[0].fields[0].value) {
            this.disableDownload = false;
          }
          this.info.push({
            id: field.id,
            value: field.value,
            name: field.readableValue,
          });
        }
        this.canImport = this.info.length == this.controls.length;
      });
    }
  }

  public onFilesSelected(files: File[]) {
    this.file = files[0];
  }

  public download() {
    let id = this.sections[0]?.fields[0]?.value;
    let name = this.sections[0]?.fields[0]?.readableValue;
    this.openViewTemplate(id, name);
  }

  public cancel() {
    this.dialogRef.close(null);
  }

  public import() {
    this.dialogRef.close({
      data: this.info,
      file: this.file,
    });
  }

  async openViewTemplate(id: string, name: string) {
    const loadingDialogRef = this.openLoadingDialog();
    try {
      await this.presetService.downloadTemplate(name, id);
    } finally {
      loadingDialogRef.close();
    }
  }

  private openLoadingDialog() {
    return this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: "Downloading template",
    });
  }

  private fillDefaultForm(): void {
    const formScreens = this.controls.find(
      (control) => control.id === "formId",
    ).options;
    const formField = this.sections[0].fields.find(
      (field) => field.id === "formId",
    );
    if (formField) {
      formField.value = this.defaultFormId;
      formField.readableValue = formScreens.find(
        (screenId) => screenId.id === formField.value,
      )?.name;
      formField.valid = true;
      this.canImport = true;
      if (this.sections[0].fields[0].value) {
        this.disableDownload = false;
      }
      this.info.push({
        id: formField.id,
        value: formField.value,
        name: formField.readableValue,
      });
    }
  }
}
